import React, { useEffect, useRef } from "react";
import Balloon from "./Balloon";
import { BoltIcon } from "../ui-icons";

const F = { fontFamily: "'Fredoka', sans-serif" };

const CELEBRATION_PARTICLES = Array.from({ length: 14 }, (_, index) => ({
  left: `${8 + ((index * 19) % 84)}%`,
  delay: (index % 5) * 35,
  color: ["#FFD84D", "#41C7FF", "#A66BFF", "#FF7A8A", "#FFFFFF"][index % 5],
}));

// The shared play field for both bubble games: renders bubbles, pop bursts and
// floating scores, handles tap/swipe popping (onPopAt receives field-local
// coordinates on pointer down AND move, like the iOS DragGesture), and hosts
// the super-pop meter. Everything themed (background, rush colours) comes from
// the page.
const BubbleCanvas = ({
  bubbles,
  effects,
  onPop,
  onPopAt,
  onAreaChange,
  comboBanner,
  resetSignal,
  celebrationSignal,
  isFever,
  isGoldRush,
  rushHue,
  rushBannerVisible,
  superCharge,
  onSuperPop,
  isRunning,
  background,
  popInDuration,
  popInFrom,
  // Swipe-popping: the colour chase pops on drag (iOS DragGesture.onChanged);
  // the math game pops on tap only.
  popOnMove = true,
  children,
}) => {
  const canvasRef = useRef(null);
  const pointerActiveRef = useRef(false);

  useEffect(() => {
    const element = canvasRef.current;
    if (!element) return undefined;

    const reportSize = () => {
      const rect = element.getBoundingClientRect();
      onAreaChange({ width: rect.width, height: rect.height });
    };
    reportSize();

    const observer = new ResizeObserver(reportSize);
    observer.observe(element);
    return () => observer.disconnect();
  }, [onAreaChange]);

  const popAtEvent = (event) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect || !onPopAt) return;
    onPopAt({ x: event.clientX - rect.left, y: event.clientY - rect.top });
  };

  const rushBase = rushHue?.base || "#FFD84D";
  const rushDeep = rushHue?.deep || "#E9A300";

  return (
    <section
      ref={canvasRef}
      className="absolute inset-0 overflow-hidden select-none"
      style={{
        background: background || "linear-gradient(180deg, #EDF7FF 0%, #D6EBFF 100%)",
        animation:
          resetSignal > 0
            ? `bubbleScreenShake${resetSignal % 2} 360ms ease-out`
            : "none",
        touchAction: "none",
      }}
      onPointerDown={(event) => {
        if (!isRunning || !onPopAt) return;
        pointerActiveRef.current = true;
        event.currentTarget.setPointerCapture?.(event.pointerId);
        popAtEvent(event);
      }}
      onPointerMove={(event) => {
        if (isRunning && onPopAt && popOnMove && pointerActiveRef.current) popAtEvent(event);
      }}
      onPointerUp={() => {
        pointerActiveRef.current = false;
      }}
      onPointerCancel={() => {
        pointerActiveRef.current = false;
      }}
    >
      {/* Warm wash while FEVER is active. */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background: "linear-gradient(180deg, rgba(255,168,71,0.30), rgba(255,92,112,0.26))",
            opacity: isFever ? 1 : 0,
            transition: "opacity 450ms ease",
          }}
        />
      </div>

      {isGoldRush && (
        <div
          className="absolute inset-0 z-20 pointer-events-none bubble-rush-edge"
          style={{ "--rush-base": rushBase, "--rush-deep": rushDeep }}
        />
      )}

      {comboBanner && (
        <div className="absolute top-[28%] left-1/2 -translate-x-1/2 z-40 pointer-events-none">
          <span
            key={comboBanner.key}
            className="px-6 py-2.5 rounded-full text-2xl sm:text-3xl inline-block whitespace-nowrap"
            style={{
              ...F,
              color: "#FFFFFF",
              fontWeight: 700,
              background: "linear-gradient(180deg, #30C4FF 0%, #386FE8 100%)",
              boxShadow: "0 5px 0 rgba(29,76,158,0.4), 0 12px 24px rgba(11,26,51,0.22)",
              animation: "bubbleComboPop 900ms ease-out forwards",
            }}
          >
            {comboBanner.text}
          </span>
        </div>
      )}

      {isGoldRush && rushBannerVisible && (
        <div className="absolute top-[120px] left-1/2 -translate-x-1/2 z-30 pointer-events-none">
          <span
            className="bubble-rush-banner"
            style={{
              ...F,
              background: `linear-gradient(135deg, ${rushBase}, ${rushDeep})`,
              boxShadow: `0 5px 0 ${rushDeep}, 0 10px 22px ${rushDeep}55`,
            }}
          >
            <BoltIcon size={16} color="#FFF" /> BUBBLE RUSH! <BoltIcon size={16} color="#FFF" />
          </span>
        </div>
      )}

      <div className="absolute inset-0 overflow-hidden">
        {bubbles.map((bubble) => (
          <Balloon
            key={bubble.id}
            bubble={bubble}
            onPop={onPop}
            popInDuration={popInDuration}
            popInFrom={popInFrom}
          />
        ))}

        {effects.map((effect) => (
          <div
            key={effect.id}
            className="absolute pointer-events-none z-30"
            style={{ left: effect.x, top: effect.y }}
          >
            <span
              className="absolute rounded-full border-[3px] bubble-burst-ring"
              style={{
                width: effect.size,
                height: effect.size,
                left: -effect.size / 2,
                top: -effect.size / 2,
                borderColor: effect.color,
                borderWidth: effect.big ? 4 : 3,
                "--burst-scale": effect.big ? 1.9 : 1.45,
              }}
            />
            {effect.text && (
              <span
                className="absolute whitespace-nowrap bubble-floating-score"
                style={{
                  ...F,
                  color: effect.color,
                  fontWeight: 700,
                  fontSize: effect.big ? 26 : 20,
                  transform: "translateX(-50%)",
                  textShadow: "0 2px 2px rgba(255,255,255,0.95)",
                }}
              >
                {effect.text}
              </span>
            )}
          </div>
        ))}
      </div>

      {children}

      {celebrationSignal > 0 && (
        <div key={celebrationSignal} className="absolute inset-0 z-30 pointer-events-none overflow-hidden">
          {CELEBRATION_PARTICLES.map((particle, index) => (
            <span
              key={`${celebrationSignal}-${index}`}
              className="absolute top-[-18px] w-2.5 h-3.5 rounded-sm bubble-celebration-particle"
              style={{
                left: particle.left,
                background: particle.color,
                animationDelay: `${particle.delay}ms`,
                "--particle-drift": `${(index % 2 ? 1 : -1) * (18 + (index % 4) * 9)}px`,
              }}
            />
          ))}
        </div>
      )}

      {isRunning && onSuperPop && superCharge > 0 && (
        <div
          className="absolute left-1/2 -translate-x-1/2 z-40"
          style={{ bottom: "max(14px, env(safe-area-inset-bottom))" }}
        >
          {superCharge >= 1 ? (
            <button
              type="button"
              onPointerDown={(event) => event.stopPropagation()}
              onClick={onSuperPop}
              className="bubble-super-ready h-13 px-7 py-3 rounded-full text-white text-lg font-bold"
              style={{
                ...F,
                background: "linear-gradient(180deg, #7A5CF0 0%, #6140DB 100%)",
                border: "2px solid rgba(255,255,255,0.8)",
              }}
            >
              ✦ SUPER POP!
            </button>
          ) : (
            <div
              className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white/90 pointer-events-none"
              style={{ boxShadow: "0 4px 12px rgba(11,26,51,0.12)" }}
            >
              <BoltIcon size={14} />
              <div className="w-[120px] h-2.5 rounded-full bg-[#6140DB]/15 overflow-hidden">
                <div
                  className="h-full rounded-full transition-[width] duration-200"
                  style={{
                    width: `${Math.max(5, superCharge * 100)}%`,
                    background: "linear-gradient(90deg, #7A5CF0, #6140DB)",
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <style>{`
        @keyframes bubbleScreenShake0 {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(-7px, 4px); }
          45% { transform: translate(7px, -4px); }
          70% { transform: translate(-4px, 2px); }
        }
        @keyframes bubbleScreenShake1 {
          0%, 100% { transform: translate(0, 0); }
          20% { transform: translate(6px, -5px); }
          45% { transform: translate(-7px, 4px); }
          70% { transform: translate(4px, -2px); }
        }
        @keyframes bubbleComboPop {
          0% { transform: scale(0.55) rotate(-7deg); opacity: 0; }
          25% { transform: scale(1.08) rotate(0); opacity: 1; }
          72% { transform: scale(1); opacity: 1; }
          100% { transform: scale(0.92) translateY(-8px); opacity: 0; }
        }
        @keyframes bubbleBurstRing {
          from { transform: scale(0.5); opacity: 0.95; }
          to { transform: scale(var(--burst-scale)); opacity: 0; }
        }
        @keyframes bubbleScoreRise {
          0% { transform: translate(-50%, 0) scale(0.55); opacity: 1; }
          30% { transform: translate(-50%, -12px) scale(1.08); opacity: 1; }
          100% { transform: translate(-50%, -56px) scale(1); opacity: 0; }
        }
        @keyframes bubbleCelebrationFall {
          from { transform: translate(0, 0) rotate(0); opacity: 1; }
          to { transform: translate(var(--particle-drift), 620px) rotate(500deg); opacity: 0; }
        }
        @keyframes bubbleRushPulse {
          from { opacity: 0.45; }
          to { opacity: 0.95; }
        }
        @keyframes bubbleRushBanner {
          from { transform: scale(0.98); filter: brightness(1); }
          to { transform: scale(1.06); filter: brightness(1.12); }
        }
        @keyframes bubbleSuperPulse {
          from { transform: scale(0.97); box-shadow: 0 5px 0 #4C2FB8, 0 8px 18px rgba(97,64,219,0.38); }
          to { transform: scale(1.06); box-shadow: 0 5px 0 #4C2FB8, 0 10px 28px rgba(97,64,219,0.66); }
        }
        @keyframes bubbleRainbowPulse {
          from { scale: 0.97; }
          to { scale: 1.06; }
        }
        .bubble-burst-ring { animation: bubbleBurstRing 480ms ease-out forwards; }
        .bubble-floating-score { animation: bubbleScoreRise 720ms ease-out forwards; }
        .bubble-celebration-particle { animation: bubbleCelebrationFall 900ms ease-in forwards; }
        .bubble-rush-edge {
          border: 10px solid var(--rush-base);
          filter: blur(6px);
          animation: bubbleRushPulse 350ms ease-in-out infinite alternate;
        }
        .bubble-rush-banner {
          display: inline-block;
          padding: 9px 22px;
          border-radius: 9999px;
          border: 2px solid rgba(255,255,255,0.85);
          color: white;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.04em;
          animation: bubbleRushBanner 500ms ease-in-out infinite alternate;
        }
        .bubble-super-ready { animation: bubbleSuperPulse 550ms ease-in-out infinite alternate; }
        .bubble-rainbow-pulse { animation: bubbleRainbowPulse 600ms ease-in-out infinite alternate; }
      `}</style>
    </section>
  );
};

export default BubbleCanvas;
